/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { CrudListType, CrudTemplate, ListColumns } from 'app/components/organisms/crud/form-types';

/**
 * `LedgerEntry` type.
 */

type LedgerEntry = {
  id: string;
  pharmacyId: string;
  amount: number;
  balanceAfter: number;
  type: 'credit' | 'debit' | 'correction';
  reason: string;
  referenceId: string | null;
  referenceType: string | null;
  createdAt: string;
};

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns<LedgerEntry> => [
  {
    title: translate('common.table.columns.type'),
    dataIndex: 'type',
    key: 'type',
    size: '100px',
    render: item => item.type
  },
  {
    title: translate('common.table.columns.amount'),
    dataIndex: 'amount',
    key: 'amount',
    size: '100px',
    render: item => item.amount
  },
  {
    title: translate('common.table.columns.balanceAfter'),
    dataIndex: 'balanceAfter',
    key: 'balanceAfter',
    size: '120px',
    render: item => item.balanceAfter
  },
  {
    title: translate('common.table.columns.reason'),
    dataIndex: 'reason',
    key: 'reason',
    size: '1fr',
    render: item => item.reason
  },
  {
    title: translate('common.table.columns.referenceType'),
    dataIndex: 'referenceType',
    key: 'referenceType',
    size: '140px',
    render: item => item.referenceType ?? '—'
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '180px',
    render: item => <DateTime date={item.createdAt} />
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<LedgerEntry> {
  return {
    columns: listColumns(translate),
    route: appRoutes.dashboard.services.pharmacies.ledger,
    canEdit: false,
    canRemove: false,
    key: ['pharmacyLedger'],
    resolveParams: (params: any) => ({ pharmacyId: params?.pharmacyId }),
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100]
    },
    endpoint: apiEndpoints.pharmacyPlan.ledger as any
  };
}

/**
 * Export `createPharmacyLedgerTemplate`.
 */

export function createPharmacyLedgerTemplate(translate: TFunction): CrudTemplate<LedgerEntry> {
  return {
    list: list(translate)
  };
}
